import { memo, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";

import { Loader, PaginationAction } from "components";

import { ROWS_PER_PAGE_OPTIONS, NO_DATA_MESSAGE } from "../constants";
import UploadRowsTableRow from "./UploadRowsTableRow";

const STICKY_CENTER_SX = {
  position: "sticky",
  left: "50%",
  transform: "translateX(-50%)",
  width: "fit-content",
};

const UploadRowsTable = ({
  rows,
  columns,
  hiddenColumns,
  isLoading,
  pagination,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const visibleColumns = useMemo(
    () => (columns ?? []).filter(({ key }) => !hiddenColumns.has(key)),
    [columns, hiddenColumns],
  );
  // at least 1 so the loader/no-data row has a valid colSpan before columns arrive
  const columnCount = Math.max(visibleColumns.length, 1);

  let tableBodyContent;

  if (isLoading) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={columnCount}>
          <Box
            sx={{
              minHeight: "20vh",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box sx={STICKY_CENTER_SX}>
              <Loader />
            </Box>
          </Box>
        </TableCell>
      </TableRow>
    );
  } else if (rows?.length > 0) {
    tableBodyContent = rows.map((row, index) => (
      <UploadRowsTableRow
        key={index}
        row={row}
        visibleColumns={visibleColumns}
      />
    ));
  } else {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={columnCount} sx={{ py: 6 }}>
          <Box sx={STICKY_CENTER_SX}>
            <Typography color="text.secondary">{NO_DATA_MESSAGE}</Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableContainer sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {visibleColumns.map(({ key, label }) => (
              <TableCell key={key}>
                <Typography variant="caption" fontWeight={700}>
                  {label}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{tableBodyContent}</TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              colSpan={columnCount}
              count={pagination.totalCount}
              page={pagination.page}
              rowsPerPage={pagination.rowsPerPage}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              onPageChange={onPageChange}
              onRowsPerPageChange={onRowsPerPageChange}
              ActionsComponent={PaginationAction}
              sx={{ borderBottom: 0 }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

UploadRowsTable.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string, label: PropTypes.string }),
  ).isRequired,
  hiddenColumns: PropTypes.instanceOf(Set).isRequired,
  isLoading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
  }).isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
};

export default memo(UploadRowsTable);
